export interface SharedPuzzle {
  id: string;
  serverId?: string; // Server puzzle ID (e.g., X1234567) - for uploaded puzzles
  imageUrl: string; // On web: base64 data URL, On iOS/Android: file URI - for local/default puzzles
  difficulty: number; // Grid size
  seed: number; // Seeded RNG seed (0 = use Math.random)
  title: string;
  collectionId: string;
  addedAt: number;
}

export interface PuzzleCollection {
  id: string;
  name: string;
  puzzles: SharedPuzzle[];
  createdAt: number;
  isDefault: boolean;
}

export class CollectionManager {
  private static readonly STORAGE_KEY = 'joinsaw_collections';
  private static readonly DEFAULT_COLLECTION_ID = 'default_puppies';

  static getCollections(): PuzzleCollection[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        // Initialize with default collection on first load
        return this.initializeDefaultCollections();
      }
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error loading collections', error);
      return [];
    }
  }

  static getCollectionById(id: string): PuzzleCollection | undefined {
    const collections = this.getCollections();
    return collections.find(c => c.id === id);
  }

  static createCollection(name: string): PuzzleCollection | null {
    const collections = this.getCollections();

    const newCollection: PuzzleCollection = {
      id: 'collection_' + Date.now().toString() + Math.random().toString(36).substr(2, 9),
      name: name,
      puzzles: [],
      createdAt: Date.now(),
      isDefault: false
    };

    collections.push(newCollection);

    const saved = this.saveCollections(collections);
    if (!saved) return null;
    return newCollection;
  }

  static deleteCollection(collectionId: string): boolean {
    const collections = this.getCollections();
    const collection = collections.find(c => c.id === collectionId);

    // Don't allow deleting default collection
    if (collection?.isDefault) {
      console.warn('Cannot delete default collection');
      return false;
    }

    const filtered = collections.filter(c => c.id !== collectionId);
    return this.saveCollections(filtered);
  }

  static addPuzzleToCollection(collectionId: string, imageUrl: string, title: string, difficulty: number = 4, serverId?: string): SharedPuzzle | null {
    const collections = this.getCollections();
    const collection = collections.find(c => c.id === collectionId);

    if (!collection) {
      console.error('Collection not found');
      return null;
    }

    const newPuzzle: SharedPuzzle = {
      id: 'puzzle_' + Date.now().toString() + Math.random().toString(36).substr(2, 9),
      serverId: serverId,
      imageUrl: imageUrl,
      difficulty: difficulty,
      seed: 0,
      title: title,
      collectionId: collectionId,
      addedAt: Date.now()
    };

    collection.puzzles.push(newPuzzle);

    const saved = this.saveCollections(collections);
    if (!saved) return null;
    return newPuzzle;
  }

  static deletePuzzleFromCollection(collectionId: string, puzzleId: string): boolean {
    const collections = this.getCollections();
    const collection = collections.find(c => c.id === collectionId);

    if (!collection) {
      console.error('Collection not found');
      return false;
    }

    // Remove puzzle from collection
    collection.puzzles = collection.puzzles.filter(p => p.id !== puzzleId);

    // Auto-delete collection if empty and not default
    if (collection.puzzles.length === 0 && !collection.isDefault) {
      return this.deleteCollection(collectionId);
    }

    return this.saveCollections(collections);
  }

  static getPuzzleById(puzzleId: string): SharedPuzzle | undefined {
    const collections = this.getCollections();
    for (const collection of collections) {
      const puzzle = collection.puzzles.find(p => p.id === puzzleId);
      if (puzzle) return puzzle;
    }
    return undefined;
  }

  static getDefaultPuppyImages(): string[] {
    return [
      '/images/puppies/puppy1.jpg',
      '/images/puppies/puppy2.jpg',
      '/images/puppies/puppy3.jpg',
      '/images/puppies/puppy4.jpg',
      '/images/puppies/puppy5.jpg',
      '/images/puppies/puppy6.jpg',
      '/images/puppies/puppy7.jpg',
      '/images/puppies/puppy8.jpg',
      '/images/puppies/puppy9.jpg',
      '/images/puppies/puppy10.jpg',
    ];
  }

  private static initializeDefaultCollections(): PuzzleCollection[] {
    const puppyImages = this.getDefaultPuppyImages();
    const defaultPuzzles: SharedPuzzle[] = puppyImages.map((imageUrl, index) => ({
      id: `default_puppy_${index + 1}`,
      imageUrl: imageUrl,
      difficulty: 4,
      seed: 0,
      title: `Puppy ${index + 1}`,
      collectionId: this.DEFAULT_COLLECTION_ID,
      addedAt: Date.now() + index
    }));

    const defaultCollection: PuzzleCollection = {
      id: this.DEFAULT_COLLECTION_ID,
      name: 'Puppies',
      puzzles: defaultPuzzles,
      createdAt: Date.now(),
      isDefault: true
    };

    const collections = [defaultCollection];
    this.saveCollections(collections);
    return collections;
  }

  private static saveCollections(collections: PuzzleCollection[]): boolean {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(collections));
      return true;
    } catch (error) {
      if (error instanceof DOMException && (
        error.name === 'QuotaExceededError' ||
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      )) {
        console.error('Storage quota exceeded');
        return false;
      }
      console.error('Error saving collections', error);
      return false;
    }
  }
}
